import { DocumentNode } from 'graphql';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetOrderQuery as GetOrder } from '__generated__/graphql';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import GetOrderQuery from './GetOrderQuery';
import GetParticipantOrdersQuery from './GetParticipantOrdersQuery';
import RestoreOrderMutation from './RestoreOrderMutation';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';

type Order = GetOrder['order'];

export interface RestoreOrderFormProps {
  order: Order;
  refetchQueries: DocumentNode[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const RestoreOrderForm = ({
  order, onSuccess, onCancel, refetchQueries = [GetParticipantOrdersQuery, GetOrderQuery],
}: RestoreOrderFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const [restoreOrder, { loading }] = useMutation(
    RestoreOrderMutation,
    {
      variables: {
        id: order.id,
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.order_restored'));
      },
    },
  );

  return (
    <UI.Form onSubmit={() => restoreOrder()}>
      <UI.FormGrid>
        <UI.H4>
          {t('restore_order')}
        </UI.H4>
        <UI.Paragraph>
          {t('restore_order_description')}
        </UI.Paragraph>
        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} disabled={loading}>
              {t('cancel')}
            </UI.Button>
          )}
          {' '}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={loading}
          >
            {t('restore_order')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};
interface UseRestoreOrderModalProps {
  order: Order;
  refetchQueries?: DocumentNode[];
}

const useRestoreOrderModal = ({ order, refetchQueries }: UseRestoreOrderModalProps) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal close={closeModal}>
        {!order && <UI.PageLoader />}
        {order && (
          <RestoreOrderForm
            order={order}
            refetchQueries={refetchQueries}
            onSuccess={closeModal}
            onCancel={closeModal}
          />
        )}
      </UI.Modal>
    ),
    [order],
  );

  return [openModal, closeModal];
};

export default useRestoreOrderModal;
